import { useId, useState } from 'react';
import type { SupportedCountry } from '@/constants/country';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';
import IconButton from '../Button/IconButton';
import Combobox from '../Combobox';
import Icon from '../Icon/Icon';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useStateProvinces } from './useStateProvinces';
export interface SelectStateProvinceProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  country?: SupportedCountry;
  value?: string;
  onValueChange: (value: string) => void;
  width?: number;
  /** クリアボタン押下時の処理（任意項目でのみ渡す想定） */
  onClear?: () => void;
  /** ルート要素に付与する追加クラス名（w-full 等で幅を上書き可能） */
  rootClassName?: string;
}
interface StateProvince {
  key?: string;
  label: string;
  value: string;
  disabled?: boolean;
}
const SelectStateProvince = ({
  country,
  value,
  onValueChange,
  width,
  onClear,
  rootClassName
}: SelectStateProvinceProps) => {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'component.selectStateProvince'
  });
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const popoverContentId = useId();
  const {
    stateProvinces = [],
    isLoading
  } = useStateProvinces(country);
  const selectedStateProvince = stateProvinces.find(c => c.value === value);
  const canClear = Boolean(onClear && !isLoading && value);
  const handleStateProvinceChange = (value: string | null) => {
    onValueChange(value ?? '');
    if (value) {
      setOpen(false);
    }
  };
  return <Popover open={open} onOpenChange={setOpen}>
      <div className={cn('relative inline-block', rootClassName)} style={rootClassName ? undefined : {
      width: width ?? 176
    }}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" aria-expanded={open} aria-controls={popoverContentId} className="relative w-full justify-start pr-10" disabled={isLoading}>
            <span className={canClear ? 'min-w-0 truncate pr-8' : 'min-w-0 truncate'}>
              {selectedStateProvince?.label || t('placeholder')}
            </span>
            <div className="absolute inset-y-0 right-3 flex items-center">
              <Icon iconName="ChevronDownIcon" className="shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        {canClear && <div className="absolute inset-y-0 right-8 flex items-center">
            <IconButton icon="XIcon" variant="mutedGhost" size="ExtraSmall" aria-label="Clear selection" onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          onClear?.();
          setOpen(false);
          setSearchText('');
        }} onPointerDown={e => {
          e.preventDefault();
          e.stopPropagation();
        }} />
          </div>}
      </div>
      <PopoverContent id={popoverContentId} className="w-60 p-0">
        <Combobox<StateProvince> type="single" options={stateProvinces} value={selectedStateProvince?.value || ''} onValueChange={handleStateProvinceChange} search={searchText} onSearchChange={setSearchText} searchPlaceholder={t('searchPlaceholder')} noResultsMessage={t('noResultsMessage')} showCheckbox={false} />
      </PopoverContent>
    </Popover>;
};
export default SelectStateProvince;